import { queryOptions, useQuery } from '@tanstack/react-query';

import type { DateRange } from '@cast/shared-types';
import { WorkloadLabelFilters } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOADS_LABEL_NAMES_COST_REPORT_QUERY_KEY =
  'workloads-label-names-cost-report';

const getWorkloadLabelNamesQueryKey = ({
  startTime,
  endTime,
  filter,
}: { startTime?: string; endTime?: string; filter?: string } = {}) =>
  [
    WORKLOADS_LABEL_NAMES_COST_REPORT_QUERY_KEY,
    ...(startTime !== undefined ? [startTime, endTime, filter] : []),
  ] as const;

type WorkloadLabelNameQuery = {
  range: DateRange;
  filter?: WorkloadLabelFilters;
  enabled?: boolean;
};

type UseWorkloadLabelNamesQuery = {
  isLoading: boolean;
  labelNames?: string[];
  error?: unknown;
  refetch: () => void;
};

const getWorkloadLabelNamesQueryOptions = ({
  range: [from, to],
  filter = {},
  enabled = true,
}: WorkloadLabelNameQuery) =>
  queryOptions({
    queryKey: getWorkloadLabelNamesQueryKey({
      startTime: from.toISOString(),
      endTime: to.toISOString(),
      filter: JSON.stringify(filter),
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getWorkloadLabelNames(
        {
          startTime: from.toISOString(),
          endTime: to.toISOString(),
          labelFilters: filter,
        },
        { timeout: 30_000 }
      );

      return data.labelNames;
    },
    enabled,
  });

export const useWorkloadLabelNamesQuery = (
  props: WorkloadLabelNameQuery
): UseWorkloadLabelNamesQuery => {
  const {
    isLoading,
    data: labelNames,
    error,
    refetch,
  } = useQuery(getWorkloadLabelNamesQueryOptions(props));

  return {
    labelNames,
    isLoading,
    error,
    refetch,
  };
};
